export interface ParsedProfile {
    device?: string;
    identifiers: string[];
    raw?: boolean;
}

export function deserializeProfiles(profiles: string[] | string | undefined | null): ParsedProfile[] {
    if (!profiles) return [];

    const profileArray = Array.isArray(profiles) ? profiles : [profiles];
    const parsed: ParsedProfile[] = [];
    const rawIdentifiers: string[] = [];

    for (const p of profileArray) {
        if (!p) continue;
        try {
            // Try to parse as JSON
            const obj = JSON.parse(p);
            if (obj && typeof obj === "object" && obj.identifiers) {
                parsed.push({
                    device: obj.device,
                    identifiers: Array.isArray(obj.identifiers) ? obj.identifiers : [obj.identifiers],
                    raw: false,
                });
            } else {
                // Valid JSON but doesn't match our format, treat as raw identifier
                rawIdentifiers.push(p);
            }
        } catch (e) {
            // Not JSON, treat as raw identifier (old format)
            rawIdentifiers.push(p);
        }
    }

    if (rawIdentifiers.length > 0) {
        parsed.push({
            identifiers: rawIdentifiers,
            raw: true,
        });
    }

    return parsed;
}

export function serializeProfile(deviceLabel: string, identifiers: string[]): string {
    return JSON.stringify({
        device: deviceLabel,
        identifiers: identifiers,
    });
}

export function appendProfile(existing: string[] | string | undefined | null, deviceLabel: string, newIdentifiers: string[]): string[] {
    if (!deviceLabel) return existing ? (Array.isArray(existing) ? existing : [existing]) : [];

    const existingArray = existing ? (Array.isArray(existing) ? existing : [existing]) : [];

    // Filter out existing profile for this specific device to replace it
    const filtered = existingArray.filter((p) => {
        try {
            const obj = JSON.parse(p);
            if (obj && typeof obj === "object" && obj.device === deviceLabel) {
                return false; // Remove exact same device to replace
            }
            return true;
        } catch (e) {
            return true; // Keep raw or invalid JSON
        }
    });

    // Add the new device profile
    filtered.push(serializeProfile(deviceLabel, newIdentifiers));

    return filtered;
}

export function getAllIdentifiers(profiles: string[] | string | undefined | null): string[] {
    const parsed = deserializeProfiles(profiles);
    const all = new Set<string>();
    for (const p of parsed) {
        for (const id of p.identifiers) {
            all.add(id);
        }
    }
    return Array.from(all);
}

export function hasProfileForDevice(profiles: string[] | string | undefined | null, deviceLabel: string): boolean {
    if (!deviceLabel) return false;
    const parsed = deserializeProfiles(profiles);
    return parsed.some((p) => p.device === deviceLabel && p.identifiers.length > 0);
}
